import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Button,
    DialogProps,
    Typography,
    TextField,
    MenuItem,
    FormHelperText,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useEffect, useState} from 'react';
import instance from '../../instance';
import CallNotification from '../../context/NotificationContext';

const VAT_OPTIONS = [0, 5, 16];

type NomenclatureOptionsPopupProps = {
    open: boolean;
    userId: number | null;
    onClose: () => void;
} & Pick<DialogProps, 'fullWidth' | 'maxWidth'>;

export const NomenclatureOptionsPopup: React.FC<NomenclatureOptionsPopupProps> = ({
    open,
    userId,
    onClose,
    fullWidth = true,
    maxWidth = 'sm',
}) => {
    const {handleErrorNotification} = CallNotification();
    const [flagVat, setFlagVat] = useState<'Y' | 'N' | null>(null);
    const [vatValue, setVatValue] = useState<number | ''>('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setFlagVat(null);
        setVatValue('');
        if (!userId) return;
        void loadVatInfo();
    }, [open, userId]);

    const loadVatInfo = async () => {
        setIsLoading(true);
        try {
            const response = await instance.post('/LK', {
                method: 'GetVatInfo',
                params: {user_id: Number(userId)},
            });
            if (response?.data) {
                const flag = response.data.vat_info.flag_vat as 'Y' | 'N' | undefined;
                const defaultTax =
                    typeof response.data.vat_info.default_tax_percent === 'number' ? response.data.vat_info.default_tax_percent : null;
                setFlagVat(flag ?? null);
                setVatValue(flag === 'Y' && defaultTax !== null ? defaultTax : '');
            }
        } catch (error) {
            console.error('Ошибка при получении информации НДС', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirm = async () => {
        if (flagVat !== 'Y' || vatValue === '' || !userId) {
            onClose();
            return;
        }
        setIsSaving(true);
        try {
            const response = await instance.post('/LK', {
                method: 'SetVatInfo',
                params: {user_id: Number(userId), default_tax_percent: Number(vatValue)},
            });
            handleErrorNotification(response, true);
            if (!response.data.error) {
                onClose();
            }
        } catch (error) {
            console.error('Ошибка при сохранении информации НДС', error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth={fullWidth} maxWidth={maxWidth}>
            <Box sx={{p: '20px 24px 10px 3px', position: 'relative', backgroundColor: '#f5f7ff'}}>
                <DialogTitle sx={{pr: 5}}>Дополнительные опции номенклатуры</DialogTitle>
                <IconButton onClick={onClose} sx={{position: 'absolute', right: 16, top: 16}}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Box display="flex" flexDirection="column" gap={2} pb={1}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                Стандартный процент НДС при добавлении номенклатуры
                            </Typography>
                            <TextField
                                select
                                value={vatValue}
                                onChange={e => setVatValue(e.target.value === '' ? '' : Number(e.target.value))}
                                disabled={flagVat !== 'Y'}
                                label="Ставка НДС"
                            >
                                {VAT_OPTIONS.map(v => (
                                    <MenuItem key={v} value={v}>
                                        {v}%
                                    </MenuItem>
                                ))}
                            </TextField>
                            {flagVat !== 'Y' && <FormHelperText>Опция доступна только для плательщиков НДС</FormHelperText>}
                        </Box>
                        <Box sx={{mt: 2, mb: 1}}>
                            <Button variant="contained" onClick={() => void handleConfirm()} sx={{mr: 2}} disabled={isSaving || flagVat !== 'Y'}>
                                Сохранить
                            </Button>
                            <Button variant="text" onClick={onClose} disabled={isSaving}>
                                Отмена
                            </Button>
                        </Box>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};
